import { Box } from '@mui/material';
import React from 'react';
import FundOpportunity from '../../models/FundOpportunity';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import MultiMap from '../MultiMap';
import { StyledTypography } from '../styles/styles';
import StyledSection from '../styles/StyledSection';

type FundOpportunitySummaryGridProps = {
  fundOpportunity: FundOpportunity;
  printMode?: boolean;
};

function buildSummaryRows(fundOpportunity: FundOpportunity) {
  return [
    { label: 'Development', value: fundOpportunity?.name ?? '-' },
    {
      label: 'Location',
      value: fundOpportunity?.addresses?.[0]?.fullAddress ?? '-',
    },
    {
      label: 'Total Investment',
      value: fundOpportunity?.fundSize
        ? ExtendedLiquid.currencyMillions(fundOpportunity.fundSize)
        : '-',
    },
    {
      label: 'Investment Term',
      value: fundOpportunity?.fundDuration ? `${fundOpportunity.fundDuration} months` : '-',
    },
    {
      label: 'Forecast IRR',
      value: fundOpportunity?.interest ? `${fundOpportunity.interest}%` : '-',
    },
  ];
}

export default function FundOpportunitySummaryGrid({
  fundOpportunity,
  printMode,
}: FundOpportunitySummaryGridProps) {
  return (
    <Box
      className="summary-content"
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      alignItems="stretch"
      justifyContent="space-between"
      gap={{ xs: '1.875rem', lg: '7.5rem' }}
      mb="0.25rem"
      sx={{
        px: { xs: 0, md: '1.25rem' },
        pb: '1.25rem',
        ['@media (max-width: 1024px)']: {
          width: '100%',
          display: 'grid',
          rowGap: '1.875rem',
          gridTemplateColumns: '100%',
          gridAutoRows: printMode ? 'auto' : '1fr',
          boxSizing: 'border-box',
        },
      }}
    >
      <StyledSection
        child
        tone="tone3"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.8,
        }}
      >
        {buildSummaryRows(fundOpportunity).map(({ label, value }) => (
          <Box
            key={label}
            display="grid"
            gridTemplateColumns="minmax(120px, 40%) 1fr"
            gap={2}
            alignItems="start"
          >
            <StyledTypography fontWeight={600}>{label}</StyledTypography>
            <StyledTypography>{value}</StyledTypography>
          </Box>
        ))}
      </StyledSection>

      <StyledSection
        noTopPadding
        noSidePaddingFrom="xs"
        bottomSpacing={0}
        child
        tone="tone3"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <MultiMap
          key={fundOpportunity?.id || 'no-id'}
          addresses={fundOpportunity?.addresses ?? []}
          printMode={printMode}
        />
      </StyledSection>
    </Box>
  );
}
